import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { trackEvent } from '../../utils/visitorTracker';
import api from '../../utils/api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@bnvsrnd/brand-assets';
import logo from '../../assets/images/Duotutor_logo_black.svg';
import AdmissionInquiryModal from './AdmissionInquiryModal';
import AppFooter, { NaverBlogIcon } from '../Layout/Footer';
import './DemoLayout.css';
import './DemoAnimations.css';

/**
 * 데모 웹사이트(/demo) 공통 셸 — 로그인 불필요한 공개 페이지.
 * 상단 네비(헤더) 멀티 페이지 구조. (기존 사이드바 제거)
 */
const NAV_ITEMS = [
    // 커리큘럼은 메인 랜딩 아래 영역(#curriculum) — 이동 후 그 위치로 스크롤
    { to: '/main', label: '커리큘럼', scrollTo: 'curriculum' },
    { to: '/main/submission-example', label: '학생 포털' },
    { to: '/main/student-works', label: '학생 포트폴리오' },
    { to: '/main/school-record', label: '생기부 연계' },
];

// 회사 네이버 블로그(bnv_solutions) DuoTutor 카테고리의 최신글 — 서버가 블로그 RSS 에서 찾아 그 글로 보내 준다
// (server/routes/blog.js, 못 찾으면 카테고리 목록). 헤더 메뉴 맨 오른쪽(생기부 연계 옆)에서 새 탭으로 연다.
const COMPANY_BLOG_URL = `${api.defaults.baseURL}/blog/latest`;

function DemoLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const isActive = (to) => {
        // '/main'(커리큘럼)은 인덱스라 모든 하위경로의 접두사이므로, 커리큘럼 상세일 때만 활성화
        // 메인 랜딩(/main) 자체에선 비활성 — 단, '커리큘럼' 탭으로 커리큘럼 영역에 이동한 경우는 활성
        if (to === '/main') {
            if (location.pathname === '/main') return curriculumActive;
            return location.pathname.startsWith('/main/curriculum');
        }
        return location.pathname === to || location.pathname.startsWith(`${to}/`);
    };

    // 페이지 진입·전환 시 항상 상단에서 시작 (SPA 라우팅/브라우저 복원으로 스크롤이 남는 문제 방지)
    // 그리기 전에(layout effect) 올려야 긴 페이지(커리큘럼)에서 넘어올 때 이전 스크롤 위치로 한 번 그려졌다 튀는 현상이 없다
    // 단, 특정 영역으로 가는 이동(state.scrollTo)은 해당 페이지가 직접 스크롤하므로 건너뜀
    useLayoutEffect(() => {
        if (location.state?.scrollTo) return;
        window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // '커리큘럼' 탭으로 이동했는지 — 탭 이동(state.scrollTo) 시 켜고, 다른 페이지로 나가면 끔
    const [curriculumActive, setCurriculumActive] = useState(false);
    const prevPathRef = useRef(location.pathname);
    useEffect(() => {
        if (location.state?.scrollTo === 'curriculum') setCurriculumActive(true);
        else if (prevPathRef.current !== location.pathname) setCurriculumActive(false);
        prevPathRef.current = location.pathname;
    }, [location]);

    // 스크롤 시 헤더에 그림자 (sticky 구분) — 위로 올라가 맨 위(랜딩 히어로)에 닿으면 커리큘럼 활성도 해제
    // (탭 클릭 직후 아래로 스크롤되는 첫 프레임은 아직 맨 위 근처라, '위로 올라오는 중'일 때만 해제)
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        let lastY = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 8);
            if (y <= 8 && y < lastY) setCurriculumActive(false);
            lastY = y;
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // 도입 문의 모달
    const [inquiryOpen, setInquiryOpen] = useState(false);

    return (
        <div className="demo-shell">
            <header className={`demo-header ${scrolled ? 'scrolled' : ''}`}>
                <div className="demo-header-bar">
                    {/* 좌측: 로고 + 메뉴 (글자 하단 기준선 정렬) */}
                    <div className="demo-header-left">
                        <Link to="/main" className="demo-header-logo">
                            <img src={logo} alt="duoTutor" />
                        </Link>
                        <nav className="demo-header-nav">
                            {NAV_ITEMS.map((item) => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    state={item.scrollTo ? { scrollTo: item.scrollTo, smooth: location.pathname === item.to } : undefined}
                                    className={`demo-header-link ${isActive(item.to) ? 'active' : ''}`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                            <a
                                href={COMPANY_BLOG_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="demo-header-link demo-header-blog"
                                onClick={() => trackEvent('blog_click', { from: 'header' })}
                            >
                                <NaverBlogIcon size={16} />
                                블로그
                            </a>
                        </nav>
                    </div>
                    {/* 우측: 로그인 + 도입 문의 */}
                    <div className="demo-header-actions">
                        <Button variant="secondary" onClick={() => { trackEvent('login_click', { from: 'header' }); navigate('/login'); }}>
                            로그인
                        </Button>
                        <Button onClick={() => { trackEvent('inquiry_open', { from: 'header' }); setInquiryOpen(true); }}>
                            도입 문의
                        </Button>
                    </div>
                </div>
            </header>

            {inquiryOpen && <AdmissionInquiryModal onClose={() => setInquiryOpen(false)} />}

            <main className="demo-content">
                <div className="demo-content-inner">
                    {children}
                </div>
            </main>

            <AppFooter />
        </div>
    );
}

export default DemoLayout;
